import { useId, type HTMLAttributes, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, Minus } from 'iconsax-reactjs';
import { cx } from '../internal/cx';
import { Skeleton } from '../Skeleton/Skeleton';
import styles from './MetricCard.module.css';

export type TrendDirection = 'up' | 'down' | 'flat';
export type TrendSentiment = 'positive' | 'negative' | 'neutral';

export interface MetricDelta {
  /** Pre-formatted change, e.g. "+2.4%" or "12". */
  value: string;
  direction: TrendDirection;
  /**
   * Whether the change is good or bad. Defaults: up = positive, down = negative, flat = neutral.
   * Set explicitly for metrics where "up" is bad (e.g. chronic absenteeism).
   */
  sentiment?: TrendSentiment;
  /** Comparison context, e.g. "vs last month". */
  label?: string;
}

export type MetricTone =
  'primary' | 'info' | 'success' | 'warning' | 'danger' | 'special' | 'accent';

export interface MetricCardProps extends HTMLAttributes<HTMLDivElement> {
  label: string;
  /** Pre-formatted value (numbers use tabular numerals). */
  value: ReactNode;
  delta?: MetricDelta;
  icon?: ReactNode;
  iconTone?: MetricTone;
  helpText?: ReactNode;
  loading?: boolean;
}

const DIRECTION_TEXT: Record<TrendDirection, string> = {
  up: 'Increased by',
  down: 'Decreased by',
  flat: 'No change',
};

function defaultSentiment(direction: TrendDirection): TrendSentiment {
  if (direction === 'up') return 'positive';
  if (direction === 'down') return 'negative';
  return 'neutral';
}

export function MetricCard({
  label,
  value,
  delta,
  icon,
  iconTone = 'primary',
  helpText,
  loading = false,
  className,
  ...rest
}: MetricCardProps) {
  const labelId = useId();
  const sentiment = delta ? (delta.sentiment ?? defaultSentiment(delta.direction)) : 'neutral';
  const TrendIcon =
    delta?.direction === 'up' ? ArrowUp : delta?.direction === 'down' ? ArrowDown : Minus;

  return (
    <div
      role="group"
      aria-labelledby={labelId}
      aria-busy={loading || undefined}
      className={cx(styles.card, styles[iconTone], className)}
      {...rest}
    >
      <div className={styles.top}>
        <p id={labelId} className={styles.label}>
          {label}
        </p>
        {icon && (
          <span className={styles.icon} aria-hidden="true">
            {icon}
          </span>
        )}
      </div>
      {loading ? (
        <div className={styles.loading}>
          <Skeleton variant="rect" height="2rem" width="60%" />
          <Skeleton variant="text" width="40%" />
          <span className="sr-only">Loading</span>
        </div>
      ) : (
        <>
          <p className={styles.value}>{value}</p>
          {(delta || helpText) && (
            <div className={styles.meta}>
              {delta && (
                <span className={cx(styles.delta, styles[sentiment])}>
                  <TrendIcon size={14} color="currentColor" aria-hidden="true" />
                  <span className="sr-only">{DIRECTION_TEXT[delta.direction]}</span>
                  {delta.direction !== 'flat' || delta.value ? <span>{delta.value}</span> : null}
                </span>
              )}
              {delta?.label && <span className={styles.deltaLabel}>{delta.label}</span>}
              {helpText && <span className={styles.help}>{helpText}</span>}
            </div>
          )}
        </>
      )}
    </div>
  );
}
